import { startOfDay, startOfWeek, startOfMonth, isAfter } from 'date-fns';
import type { OrdemServico } from '@/types/ordemServico';

/** Estados/colunas lógicos das listas de OS (inclui o virtual "disponiveis"). */
export type EstadoOS =
  | 'disponiveis'
  | 'aceita'
  | 'em_execucao'
  | 'concluida'
  | 'cancelada'
  | 'recusada'
  | 'pendente';

/**
 * Agrupa OS por estado lógico. `recusada`→`cancelada`; `pendente` sem prestador→
 * `disponiveis`; `pendente` com prestador→`aceita`.
 */
export function agruparOrdensPorStatus(ordens: OrdemServico[]): Record<EstadoOS, OrdemServico[]> {
  const grupos: Record<EstadoOS, OrdemServico[]> = {
    disponiveis: [],
    aceita: [],
    em_execucao: [],
    concluida: [],
    cancelada: [],
    recusada: [],
    pendente: [],
  };
  ordens.forEach((os) => {
    if (os.status === 'recusada') {
      grupos.cancelada.push(os);
    } else if (os.status === 'pendente' && !os.prestador_id) {
      grupos.disponiveis.push(os);
    } else if (os.status === 'pendente' && os.prestador_id) {
      grupos.aceita.push(os);
    } else {
      grupos[os.status as EstadoOS].push(os);
    }
  });
  return grupos;
}

export interface FiltrosOS {
  termoBusca?: string;
  prestadorId?: string;
  periodo?: 'hoje' | 'semana' | 'mes' | 'tudo';
}

/** Filtra OS por termo de busca, prestador e período. `now` injetável para testes. */
export function filtrarOrdens(
  ordens: OrdemServico[],
  filtros: FiltrosOS,
  now: Date = new Date()
): OrdemServico[] {
  const { termoBusca = '', prestadorId, periodo = 'tudo' } = filtros;
  const cutoff =
    periodo === 'hoje'
      ? startOfDay(now)
      : periodo === 'semana'
        ? startOfWeek(now, { weekStartsOn: 1 })
        : periodo === 'mes'
          ? startOfMonth(now)
          : null;

  return ordens.filter((os) => {
    if (termoBusca) {
      const termo = termoBusca.toLowerCase();
      const match =
        os.numero_os.toLowerCase().includes(termo) ||
        os.cliente?.nome?.toLowerCase().includes(termo) ||
        os.prestador?.profile?.nome?.toLowerCase().includes(termo);
      if (!match) return false;
    }
    if (prestadorId === '__none__') {
      if (os.prestador_id) return false;
    } else if (prestadorId) {
      if (os.prestador_id !== prestadorId) return false;
    }
    if (cutoff) {
      const ref = os.data_agendada || os.criado_em;
      if (!ref) return false;
      if (!isAfter(new Date(ref), cutoff)) return false;
    }
    return true;
  });
}
